import {
  FETCH_TASKS,
  FETCH_TASKS_SUCCESS,
  FETCH_TASKS_FAILED,
  ACCEPT_TASK,
  CANCEL_TASK,
} from "../store/types";
import store from "../store/store";
import { updateProfile } from "./authactions";
import { RequestPushMsg } from "../other/NotificationFunctions";
import { firebase } from '../config/configureFirebase';
import { onValue, runTransaction, get, off, push } from "firebase/database";
import {driverQueue} from '../other/sharedFunctions';
import getCurrentUid from '../other/getCurrentUid';
import { getValidTrackingCoordinates } from '../other/trackingPoint';
import { buildDriverAcceptance, isVerifiedDriverAcceptance } from '../other/bookingAcceptance';

let tasksRetryTimer = null;

export const fetchTasks = () => (dispatch) => {
  const { auth, tasksRef } = firebase;

  const uid = getCurrentUid(auth);
  if (!uid) return;
  // Tear down any previous listener before resubscribing.
  off(tasksRef());
  if (tasksRetryTimer) {
    clearTimeout(tasksRetryTimer);
    tasksRetryTimer = null;
  }
  dispatch({
    type: FETCH_TASKS,
    payload: null,
  });
  onValue(
    tasksRef(),
    (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const arr = Object.keys(data)
          .filter(
            (i) => data[i].requestedDrivers && data[i].requestedDrivers[uid]
          )
          .map((i) => {
            data[i].id = i;
            return data[i];
          });
        dispatch({
          type: FETCH_TASKS_SUCCESS,
          payload: arr,
        });
      } else {
        // Empty snapshot is "no pending tasks", NOT a failure.
        // Keep the listener alive so the next pending booking reopens the list
        // without needing an app restart.
        dispatch({
          type: FETCH_TASKS_SUCCESS,
          payload: [],
        });
      }
    },
    (error) => {
      // Listener cancelled (e.g. permission lost, rules change, auth token gone).
      // Surface the error to the reducer and schedule a single resubscribe after 5s;
      // an infinite tight loop would spin forever on PERMISSION_DENIED.
      dispatch({
        type: FETCH_TASKS_FAILED,
        payload: (error && error.message) ||
          store.getState().languagedata.defaultLanguage.no_tasks,
      });
      if (tasksRetryTimer) {
        clearTimeout(tasksRetryTimer);
      }
      tasksRetryTimer = setTimeout(() => {
        tasksRetryTimer = null;
        fetchTasks()(dispatch);
      }, 5000);
    }
  );
};

export const acceptTask = (task) => async (dispatch) => {
  const { auth, trackingRef, singleUserRef, singleBookingRef } = firebase;

  const uid = getCurrentUid(auth);
  if (!uid) return false;

  try {
    const profileSnapshot = await get(singleUserRef(uid));
    const profile = profileSnapshot.val();
    if (!profile) return false;

    const result = await runTransaction(singleBookingRef(task.id), (booking) =>
      buildDriverAcceptance(booking, { uid, profile, task })
    );
    if (!isVerifiedDriverAcceptance(result, uid)) return false;

    updateProfile({ queue: driverQueue ? false : true })(dispatch);
    RequestPushMsg(task.customer_token, {
      title:
        store.getState().languagedata.defaultLanguage
          .notification_title,
      msg:
       profile.firstName +
        store.getState().languagedata.defaultLanguage
          .accept_booking_request,
      screen: "BookedCab",
      params: { bookingId: task.id },
    });

    const driverLocation = getValidTrackingCoordinates(
      store.getState().gpsdata.location
    );

    if (driverLocation) {
      push(trackingRef(task.id), {
        at: new Date().getTime(),
        status: "ACCEPTED",
        ...driverLocation,
      });
    } else {
      console.warn(
        '[tracking] Skipping acceptance tracking point because driver location is unavailable.',
        { bookingId: task.id }
      );
    }

    dispatch({
      type: ACCEPT_TASK,
      payload: { task: task },
    });
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const cancelTask = (bookingId) => (dispatch) => {
  const { auth, singleBookingRef } = firebase;

  const uid = getCurrentUid(auth);

  runTransaction(singleBookingRef(bookingId), (booking) => {
      if (booking && booking.requestedDrivers) {
        if (
          booking.requestedDrivers !== null &&
          Object.keys(booking.requestedDrivers).length === 1
        ) {
          booking.status = "NEW";
          booking.requestedDrivers = null;
          booking.driverEstimates = null;
          RequestPushMsg(booking.customer_token, {
            title:
              store.getState().languagedata.defaultLanguage.notification_title,
            msg:
              store.getState().languagedata.defaultLanguage.booking_cancelled +
              bookingId,
            screen: "BookedCab",
            params: { bookingId: bookingId },
          });
          dispatch({
            type: CANCEL_TASK,
            payload: { uid: uid, bookingId: bookingId },
          });
        }else{
          delete booking.requestedDrivers[uid];
        }
        if(booking.driverOffers && booking.driverOffers[uid]){
          delete booking.driverOffers[uid];
        }
        if(booking.selectedBid && booking.selectedBid.driver === uid){
          delete booking.selectedBid;
        }
        return booking;
      }
    });
};
